import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  RefreshCw,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { api } from '../../api/client';

/* ─────────────────────────────────────────── types ── */

interface FeatureFlag {
  name: string;
  value: boolean;
  description: string;
  last_modified: string;
}

interface RateLimits {
  api_global_rpm: number;
  api_auth_rpm: number;
  scheduler_posts_per_day: number;
  farm_actions_per_hour: number;
}

interface AISettings {
  litellm_endpoint: string;
  primary_model: string;
  fallback_model: string;
  max_tokens: number;
  temperature: number;
}

interface ProxySettings {
  proxysio_api_key: string;
  subscription_status: 'active' | 'inactive' | 'expired';
  usage_today: number;
}

interface NotificationSettings {
  alert_email: string;
  ban_rate_threshold: number;
  error_rate_threshold: number;
  disk_usage_threshold: number;
}

interface AdminConfig {
  feature_flags: FeatureFlag[];
  rate_limits: RateLimits;
  ai_settings: AISettings;
  proxy_settings: ProxySettings;
  notification_settings: NotificationSettings;
}

/* ─────────────────────────────── FeatureFlags tab ── */

const FLAG_KEYS: { name: string; description: string }[] = [
  { name: 'teleclaw_farm_enabled', description: 'Enable Telegram farm management module' },
  { name: 'teleclaw_exchange_enabled', description: 'Enable channel exchange marketplace' },
  { name: 'teleclaw_ai_plans', description: 'Enable AI-powered content plan generation' },
  { name: 'ai_content_service_online', description: 'AI content service endpoint reachable' },
];

function FeatureFlagsTab({
  flags,
  onToggle,
}: {
  flags: FeatureFlag[];
  onToggle: (name: string, value: boolean) => void;
}) {
  const map = Object.fromEntries(flags.map((f) => [f.name, f]));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            {['Flag', 'Enabled', 'Description', 'Last Modified'].map((h) => (
              <th key={h} className="px-4 py-3 text-left font-medium text-gray-500">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {FLAG_KEYS.map(({ name, description }) => {
            const f = map[name];
            const enabled = f?.value ?? false;
            return (
              <tr key={name} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs font-medium">{name}</td>
                <td className="px-4 py-3">
                  <button
                    role="switch"
                    aria-checked={enabled}
                    onClick={() => onToggle(name, !enabled)}
                    className={`relative inline-flex h-5 w-9 rounded-full transition-colors focus:outline-none ${
                      enabled ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
                        enabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-600">{description}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {f?.last_modified ? new Date(f.last_modified).toLocaleString() : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────── RateLimits tab ── */

function RateLimitsTab({
  limits,
  onSave,
  saving,
}: {
  limits: RateLimits;
  onSave: (data: RateLimits) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<RateLimits>(limits);
  useEffect(() => setForm(limits), [limits]);

  const fields: { key: keyof RateLimits; label: string; unit: string }[] = [
    { key: 'api_global_rpm', label: 'API Global', unit: 'req/min' },
    { key: 'api_auth_rpm', label: 'API Auth', unit: 'req/min' },
    { key: 'scheduler_posts_per_day', label: 'Scheduler Posts', unit: 'posts/day' },
    { key: 'farm_actions_per_hour', label: 'Farm Actions', unit: 'actions/hr' },
  ];

  return (
    <div className="max-w-md space-y-4">
      {fields.map(({ key, label, unit }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            <span className="text-gray-400 font-normal ml-1">({unit})</span>
          </label>
          <input
            type="number"
            className="input w-full"
            value={form[key]}
            min={0}
            onChange={(e) => setForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
          />
        </div>
      ))}
      <button
        className="btn-primary flex items-center gap-2"
        onClick={() => onSave(form)}
        disabled={saving}
      >
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
        Save Rate Limits
      </button>
    </div>
  );
}

/* ─────────────────────────────── AISettings tab ── */

function AISettingsTab({ settings }: { settings: AISettings }) {
  const fields: { key: keyof AISettings; label: string; type?: string }[] = [
    { key: 'litellm_endpoint', label: 'LiteLLM Endpoint URL' },
    { key: 'primary_model', label: 'Primary Model' },
    { key: 'fallback_model', label: 'Fallback Model' },
    { key: 'max_tokens', label: 'Max Tokens', type: 'number' },
    { key: 'temperature', label: 'Temperature', type: 'number' },
  ];

  return (
    <div className="max-w-md space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
        <p className="font-medium text-blue-800">AI Endpoint</p>
        <p className="font-mono text-blue-700 mt-0.5">https://ai.ncide.com/v1</p>
        <p className="text-blue-600 text-xs mt-1">Read-only — managed by infra</p>
      </div>

      {fields.map(({ key, label, type }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
          <input
            type={type ?? 'text'}
            className="input w-full bg-gray-50"
            value={String(settings[key])}
            readOnly
          />
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────── ProxySettings tab ── */

function ProxySettingsTab({ settings }: { settings: ProxySettings }) {
  const [showKey, setShowKey] = useState(false);

  const statusColor: Record<ProxySettings['subscription_status'], string> = {
    active: 'text-green-600 bg-green-50',
    inactive: 'text-gray-600 bg-gray-100',
    expired: 'text-red-600 bg-red-50',
  };

  const masked =
    settings.proxysio_api_key.length > 8
      ? settings.proxysio_api_key.slice(0, 4) + '••••••••' + settings.proxysio_api_key.slice(-4)
      : '••••••••••••';

  return (
    <div className="max-w-md space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          proxys.io API Key
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            className="input flex-1 font-mono"
            value={showKey ? settings.proxysio_api_key : masked}
            readOnly
          />
          <button
            className="btn-secondary px-3"
            onClick={() => setShowKey((v) => !v)}
            title={showKey ? 'Hide' : 'Show'}
          >
            {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Subscription Status
        </label>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            statusColor[settings.subscription_status]
          }`}
        >
          {settings.subscription_status.charAt(0).toUpperCase() +
            settings.subscription_status.slice(1)}
        </span>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Usage Today</label>
        <p className="text-2xl font-bold text-gray-800">
          {settings.usage_today.toLocaleString()}
          <span className="text-sm font-normal text-gray-500 ml-1">requests</span>
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────── Notifications tab ── */

function NotificationsTab({
  settings,
  onSave,
  saving,
}: {
  settings: NotificationSettings;
  onSave: (data: NotificationSettings) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<NotificationSettings>(settings);
  useEffect(() => setForm(settings), [settings]);

  return (
    <div className="max-w-md space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alert Email</label>
        <input
          type="email"
          className="input w-full"
          value={form.alert_email}
          onChange={(e) => setForm((f) => ({ ...f, alert_email: e.target.value }))}
        />
      </div>

      {(
        [
          { key: 'ban_rate_threshold', label: 'Ban Rate Threshold', unit: '%' },
          { key: 'error_rate_threshold', label: 'Error Rate Threshold', unit: '%' },
          { key: 'disk_usage_threshold', label: 'Disk Usage Threshold', unit: '%' },
        ] as { key: keyof NotificationSettings; label: string; unit: string }[]
      ).map(({ key, label, unit }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            <span className="text-gray-400 font-normal ml-1">({unit})</span>
          </label>
          <input
            type="number"
            className="input w-full"
            value={form[key] as number}
            min={0}
            max={100}
            onChange={(e) =>
              setForm((f) => ({ ...f, [key]: Number(e.target.value) }))
            }
          />
        </div>
      ))}

      <button
        className="btn-primary flex items-center gap-2"
        onClick={() => onSave(form)}
        disabled={saving}
      >
        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
        Save Notifications
      </button>
    </div>
  );
}

/* ─────────────────────────────────── main screen ── */

type TabKey = 'flags' | 'rate_limits' | 'ai' | 'proxy' | 'notifications';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'flags', label: 'Feature Flags' },
  { key: 'rate_limits', label: 'Rate Limits' },
  { key: 'ai', label: 'AI Settings' },
  { key: 'proxy', label: 'Proxy Settings' },
  { key: 'notifications', label: 'Notifications' },
];

export default function AdminConfig() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('flags');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery<AdminConfig>({
    queryKey: ['admin-config'],
    queryFn: () => api.get('/admin/config').then((r) => r.data),
  });

  const patchMutation = useMutation({
    mutationFn: (payload: Partial<AdminConfig>) =>
      api.patch('/admin/config', payload).then((r) => r.data),
    onSuccess: (newData) => {
      queryClient.setQueryData(['admin-config'], newData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  function handleToggleFlag(name: string, value: boolean) {
    if (!data) return;
    const updated = data.feature_flags.map((f) =>
      f.name === name ? { ...f, value, last_modified: new Date().toISOString() } : f
    );
    patchMutation.mutate({ feature_flags: updated });
  }

  function handleSaveRateLimits(rate_limits: RateLimits) {
    patchMutation.mutate({ rate_limits });
  }

  function handleSaveNotifications(notification_settings: NotificationSettings) {
    patchMutation.mutate({ notification_settings });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings size={20} className="text-gray-600" />
          <h1 className="text-xl font-semibold">System Configuration</h1>
          {isFetching && <RefreshCw size={14} className="animate-spin text-gray-400" />}
        </div>
        <div className="flex items-center gap-2">
          {saveSuccess && (
            <span className="flex items-center gap-1 text-green-600 text-sm">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {patchMutation.isError && (
            <span className="flex items-center gap-1 text-red-500 text-sm">
              <AlertCircle size={14} />
              Save failed
            </span>
          )}
          <button className="btn-secondary flex items-center gap-1" onClick={() => refetch()}>
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="card p-0 overflow-hidden">
        <div className="border-b flex overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="py-8 text-center text-gray-400">Loading configuration…</div>
          ) : !data ? (
            <div className="py-8 text-center text-gray-400">Failed to load configuration</div>
          ) : (
            <>
              {activeTab === 'flags' && (
                <FeatureFlagsTab flags={data.feature_flags} onToggle={handleToggleFlag} />
              )}
              {activeTab === 'rate_limits' && (
                <RateLimitsTab
                  limits={data.rate_limits}
                  onSave={handleSaveRateLimits}
                  saving={patchMutation.isPending}
                />
              )}
              {activeTab === 'ai' && <AISettingsTab settings={data.ai_settings} />}
              {activeTab === 'proxy' && <ProxySettingsTab settings={data.proxy_settings} />}
              {activeTab === 'notifications' && (
                <NotificationsTab
                  settings={data.notification_settings}
                  onSave={handleSaveNotifications}
                  saving={patchMutation.isPending}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
